import { React, useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { createHttpClient } from '../../utilities/httpclient';
import LocalStorageController from '../../utilities/lsc';
import { AbdsButton, AbdsInput, AbdsLabel } from '@abds/react-bindings';
import createUserSessionActivity from '../../utilities/usersessiontracking';
import { checkUserTokenFormat, REGEX_EMAIL, REGEX_PHONE, APPLICATION_API_ENDPOINT, APP_AUTH_TOKEN } from '../../utilities/constants';
import { createDisplayAreaMessengerContext, DisplayAreaMessenger } from '../displayareamessenger/DisplayAreaMessenger';
import OnboardingProgressBar from '../auxiliary/OnboardingProgressBar/OnboardingProgressBar';

import { useKeyPressEnter } from '../../customhooks/useKeyPressEnter';

import '../../styles/absgtable.css';
import '../../styles/certificatedetails.css';
import '../../styles/certificateselfservice.css';
import '../../styles/vendortechnicaldetails.css';

export function OnboardingVendorTechnicalDetails(props)
{
    let [response, setResponse] = useState({});
    let [name, setName] = useState('');
    let [phoneNumber, setPhoneNumber] = useState('');
    let [email, setEmail] = useState('');
    let [van, setVan] = useState('');
    let [as2, setAs2] = useState('');
    let [duns, setDuns] = useState('');
    let [dea, setDea] = useState('');
    let [hin, setHin] = useState('');
    let [purchaseOrderType, setPurchaseOrderType] = useState(0);
    let [isaId, setIsaId] = useState('');
    let [gsId, setGsId] = useState('');
    let [qualifier, setQualifier] = useState('');
    let [communicationProtocol, setCommunicationProtocol] = useState('');
    let [ediTransactionsArray, setEdiTransactionsArray] = useState([]);
    let [hasExistingDetails, setHasExistingDetails] = useState(false);
    let [disabled, setDisabled] = useState(false);

    const { pathname } = useLocation();

    const rEmailEntry = useRef(null);
    const rSubmitEmailEntry = useRef(null);
    const rClearEmailEntry = useRef(null);
    const rName = useRef(null);
    const rPhoneNumber = useRef(null);
    const rEmail = useRef(null);
    const rVan = useRef(null);
    const rAs2 = useRef(null);
    const rDuns = useRef(null);
    const rDea = useRef(null);
    const rHin = useRef(null);
    const rPurchaseOrderType = useRef(null);
    const rIsaId = useRef(null);
    const rGsId = useRef(null);
    const rQualifier = useRef(null);
    const rCommunicationProtocol = useRef(null);
    const rClearVendorTechnicalDetails = useRef(null);
    const rSubmitVendorTechnicalDetails = useRef(null);

    const [showVendorTechPanel, setShowVendorTechPanel] = useState(false);
    const [emailEntry, setEmailEntry] = useState('');
    let [messageContext, setMessageContext] = useState({});

    const { userToken } = useParams();

    const showErrorMessage = msg => {
        setMessageContext({});
        messageContext = createDisplayAreaMessengerContext();
        messageContext.error.message = msg;
        setMessageContext(messageContext);
    };

    /** FIRST PANEL */

    const onChangeEmailEntry = e => setEmailEntry(e.target.value);

    const onSubmitEmailEntry = e => {
        e.preventDefault();
        (!checkEmailEntryInputData() || getOnboardingRequestDetails());
    };

    const checkEmailEntryInputData = () => {
        setMessageContext({});
        messageContext = createDisplayAreaMessengerContext();

        if (emailEntry.trim().length === 0)
        {
            messageContext.error.details.push("Email is required");
        }
        else if (!REGEX_EMAIL.test(emailEntry))
        {
            messageContext.error.details.push("Email format is invalid");
        }
        
        if (messageContext.error.details.length > 0)
        {
            messageContext.error.message = "Errors were found in processing:";
            setMessageContext(messageContext);
            return false;
        }

        return true;
    }

    /** END FIRST PANEL */

    /** SECOND PANEL */

    const getOnboardingRequestDetails = async (intl=false) => {
        const httpclient = createHttpClient();
        const data = {
            'OnboardingRequestID': userToken,
            'Email': emailEntry
        };
        await httpclient.post(`${APPLICATION_API_ENDPOINT}/onboarding/${intl ? 'getadminrequest' : 'getrequest'}`, data).then(r => {
            if (!r?.data) {
                showErrorMessage('Unable to fetch request');
                setShowVendorTechPanel(false);
                return;
            }
            setResponse(r?.data);
            const lsc = new LocalStorageController();
            lsc.save(APP_AUTH_TOKEN, r.data.userToken?.toString());
            createUserSessionActivity(window.location.pathname);

            setHasExistingDetails(!!r.data.vendorTechDetails);
            setAs2(r.data.vendorTechDetails?.aS2 || '');
            setCommunicationProtocol(r.data.vendorTechDetails?.communicationProtocol || '');
            setDea(r.data.vendorTechDetails?.dea || '');
            setDuns(r.data.vendorTechDetails?.duns || '');
            setEdiTransactionsArray(r.data.vendorTechDetails?.ediTransactionsArray || []);
            setEmail(r.data.vendorTechDetails?.email || '');
            setGsId(r.data.vendorTechDetails?.gS_ID || '');
            setHin(r.data.vendorTechDetails?.hin || '');
            setIsaId(r.data.vendorTechDetails?.isA_ID || '');
            setName(r.data.vendorTechDetails?.name || '');
            setPhoneNumber(r.data.vendorTechDetails?.phoneNumber || '');
            setPurchaseOrderType(+(r.data.vendorTechDetails?.poType || 0));
            setQualifier(r.data.vendorTechDetails?.qualifier || '');
            setVan(r.data.vendorTechDetails?.van || '');

            setDisabled(false);
            setShowVendorTechPanel(true);
        })
        .catch(e => showErrorMessage(e.response?.data || e.message));
    };

    const onChangeAs2 = e => setAs2(e.target.value);
    const onChangeCommunicationProtocol = e => setCommunicationProtocol(e.target.value);
    const onChangeDea = e => setDea(e.target.value);
    const onChangeDuns = e => setDuns(e.target.value);
    const onChangeEmail = e => setEmail(e.target.value);
    const onChangeGsId = e => setGsId(e.target.value);
    const onChangeHin = e => setHin(e.target.value);
    const onChangeIsaId = e => setIsaId(e.target.value);
    const onChangeName = e => setName(e.target.value);
    const onChangePhoneNumber = e => setPhoneNumber(e.target.value);
    const onChangePurchaseOrderType = e => setPurchaseOrderType(+e.target.value);
    const onChangeQualifier = e => setQualifier(e.target.value);
    const onChangeVan = e => setVan(e.target.value);

    const onClickClearVendorTechnicalDetails = e => {
        e.preventDefault();
        setAs2('');
        setCommunicationProtocol('');
        setDea('');
        setDuns('');
        setEmail('');
        setGsId('');
        setHin('');
        setIsaId('');
        setName('');
        setPhoneNumber('');
        setPurchaseOrderType(0);
        setQualifier('');
        setVan('');
        setEdiTransactionsArray([]);
        !hasExistingDetails || getOnboardingRequestDetails();
    };

    const generateEdiTransactionCheckBoxList = () => {
        const transactions = [850,855,856,810,812,852,867,844,845,849,820];
        const sortedTransactions = transactions.sort((a, b) => a - b);
        return sortedTransactions.map(edi => {
            return (<div className="editransactionlistitem" key={`editrx${edi}`}>
                <input type="checkbox" key={edi} value={edi.toString()} onChange={e => {
                    const value = +e.target.value;
                    setEdiTransactionsArray(prev => {
                        return (e.target.checked)
                            ? [...prev, value]
                            : prev.filter(v => v !== value);
                    });
                }} checked={ediTransactionsArray?.includes(edi)} />
                <span data-role="editransactionlabel">{edi}</span>
            </div>);
        });
    };

    const onSubmitVendorTechnicalDetails = e => {
        e.preventDefault();
        const data = {
            'AS2': as2,
            'CommunicationProtocol': communicationProtocol,
            'DEA': dea,
            'DUNS': duns,
            'EdiTransactionArray': ediTransactionsArray,
            'Email': email,
            'GS_ID': gsId,
            'HIN': hin,
            'ISA_ID': isaId,
            'Name': name,
            'OnboardingRequestID': userToken,
            'PhoneNumber': phoneNumber,
            'POType': purchaseOrderType,
            'Qualifier': qualifier,
            'VAN': van
        };

        if (!checkVendorTechDetailsInputData(data))
            return;

        setDisabled(true);
        const httpclient = createHttpClient();
        httpclient.post(`${APPLICATION_API_ENDPOINT}/onboarding/savevendortechdetails`, data).then(response => {
            const context = createDisplayAreaMessengerContext();
            context.success.message = "Vendor technical details submitted successfully.";
            setMessageContext(context);
        }).catch(e => {
            showErrorMessage(e.response?.data || e.message);
            setDisabled(false);
        });
    };

    const checkVendorTechDetailsInputData = data => {
        const context = createDisplayAreaMessengerContext();

        if (!data || Object.values(data).every(value => value === null || value === undefined || value === ''))
        {
            context.error.message = "No data to submit";
            setMessageContext(context);
            return false;
        }

        if (data.AS2?.trim().length === 0)
            context.error.details.push("AS2 is required");

        if (data.CommunicationProtocol?.trim().length === 0)
            context.error.details.push("Communication Protocol is required");

        if (data.DEA?.trim().length === 0)
            context.error.details.push("DEA is required");

        if (data.DUNS?.trim().length === 0)
            context.error.details.push("DUNS is required");

        if (data.Email?.trim().length === 0)
        {
            context.error.details.push("Email is required");
        }
        else if (!REGEX_EMAIL.test(data.Email))
        {
            context.error.details.push("Email format is invalid");
        }

        if (data.GS_ID?.trim().length === 0)
            context.error.details.push("GS ID is required");

        if (data.HIN?.trim().length === 0)
            context.error.details.push("HIN is required");

        if (data.ISA_ID?.trim().length === 0)
            context.error.details.push("ISA ID is required");

        if (data.Name?.trim().length === 0)
            context.error.details.push("Name is required");

        if (data.PhoneNumber?.trim().length === 0)
        {
            context.error.details.push("Phone number is required");
        }
        else if (!REGEX_PHONE.test(data.PhoneNumber))
        {
            context.error.details.push("Phone number format is invalid");
        }

        if (data.POType === 0 || isNaN(+data.POType))
        {
            context.error.details.push("PO Type is required");
        }
        else if (![52,53,54,55].includes(+data.POType))
        {
            context.error.details.push("PO Type value is invalid");
        }

        if (data.Qualifier?.trim().length === 0)
            context.error.details.push("Qualifier is required");

        if (data.VAN?.trim().length === 0)
            context.error.details.push("VAN is required");

        if (context.error.details.length > 0)
            setMessageContext(context);

        return context.error.details.length === 0;
    };

    useKeyPressEnter(showVendorTechPanel ? rSubmitVendorTechnicalDetails : rSubmitEmailEntry);

    useEffect(() => {
        rEmailEntry.current?.addEventListener('abdsInput', onChangeEmailEntry);

        rAs2.current?.addEventListener('abdsInput', onChangeAs2);
        rCommunicationProtocol.current?.addEventListener('abdsInput', onChangeCommunicationProtocol);
        rDea.current?.addEventListener('abdsInput', onChangeDea);
        rDuns.current?.addEventListener('abdsInput', onChangeDuns);
        rEmail.current?.addEventListener('abdsInput', onChangeEmail);
        rGsId.current?.addEventListener('abdsInput', onChangeGsId);
        rHin.current?.addEventListener('abdsInput', onChangeHin);
        rIsaId.current?.addEventListener('abdsInput', onChangeIsaId);
        rName.current?.addEventListener('abdsInput', onChangeName);
        rPhoneNumber.current?.addEventListener('abdsInput', onChangePhoneNumber);
        rQualifier.current?.addEventListener('abdsInput', onChangeQualifier);
        rVan.current?.addEventListener('abdsInput', onChangeVan);

        return () => {
            rEmailEntry.current?.removeEventListener('abdsInput', onChangeEmailEntry);

            rAs2.current?.removeEventListener('abdsInput', onChangeAs2);
            rCommunicationProtocol.current?.removeEventListener('abdsInput', onChangeCommunicationProtocol);
            rDea.current?.removeEventListener('abdsInput', onChangeDea);
            rDuns.current?.removeEventListener('abdsInput', onChangeDuns);
            rEmail.current?.removeEventListener('abdsInput', onChangeEmail);
            rGsId.current?.removeEventListener('abdsInput', onChangeGsId);
            rHin.current?.removeEventListener('abdsInput', onChangeHin);
            rIsaId.current?.removeEventListener('abdsInput', onChangeIsaId);
            rName.current?.removeEventListener('abdsInput', onChangeName);
            rPhoneNumber.current?.removeEventListener('abdsInput', onChangePhoneNumber);
            rQualifier.current?.removeEventListener('abdsInput', onChangeQualifier);
            rVan.current?.removeEventListener('abdsInput', onChangeVan);
        };

    }, [showVendorTechPanel, rEmailEntry, rAs2, rCommunicationProtocol, rDea, rDuns, rEmail, rGsId, rHin, rIsaId, rName, rPhoneNumber, rQualifier, rVan]);

    useEffect(() => {
        const hasValidToken = checkUserTokenFormat(userToken);
        const fd = pathname?.toLowerCase().includes('vendordetailchange');
        setShowVendorTechPanel(fd);

        if (hasValidToken && fd)
            getOnboardingRequestDetails(true);
    }, [pathname]);

    useEffect(() => {
        if (checkUserTokenFormat(userToken))
            return;

        const messageContext = createDisplayAreaMessengerContext();
        messageContext.error.message = 'Please click on the link from your email to load this page properly.';
        setMessageContext(messageContext);
    }, []);

    return (
        <div>
            <h3>Onboarding: Vendor Technical Details</h3>

            <hr className="abcblue" />

            <DisplayAreaMessenger context={messageContext} />

            {!showVendorTechPanel && (
                <div className="vendor-tech-panel">
                    <p>Please enter the email associated with this onboarding event</p>
                    <div>
                        <AbdsInput
                            type="text"
                            required
                            width="full"
                            name="obemailentry"
                            ref={rEmailEntry}
                            value={emailEntry}
                            placeholder="Enter Associated Email Address Here">Enter Associated Email Address Here
                    </AbdsInput></div>

                    <hr />

                    <div className="buttoncontainer">
                        <AbdsButton name="cancelsettings" ref={rClearEmailEntry} onClick={() => setEmailEntry('')} appearance="outline" palette="brand">Clear</AbdsButton>
                        &nbsp;&nbsp;&nbsp;
                        <AbdsButton onClick={onSubmitEmailEntry} ref={rSubmitEmailEntry}>Submit</AbdsButton>
                    </div>
                </div>
            )}

            {showVendorTechPanel && (<>

                <OnboardingProgressBar status={response.status} />

                <table data-role="subdetailheader">
                    <thead>
                        <tr>
                            <th>Vendor Name:</th>
                            <th>Vendor Number:</th>
                            <th>Vendor Phone:</th>
                            <th>Created By:</th>
                            <th>Created Date:</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{response.vendorName}</td>
                            <td>{response.vendorNumber}</td>
                            <td>{response.vendorPhone}</td>
                            <td>{response.createdByUserName}</td>
                            <td>{response.createdDateFormatted}</td>
                        </tr>
                    </tbody>
                </table>
                <hr className="abcblue"></hr>

                <fieldset className="vendor-tech">
                    <legend>EDI Contact Information</legend>

                    <table className="absgtable noalternatecolor">
                        <tbody>
                            <tr>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="name"
                                        ref={rName}
                                        value={name}
                                        tooltip="Enter Name Here"
                                        placeholder="Enter Name Here">Name
                                    </AbdsInput></td>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="phonenumber"
                                        ref={rPhoneNumber}
                                        value={phoneNumber}
                                        tooltip="Enter Phone Number Here"
                                        placeholder="Enter Phone Number Here">Phone Number
                                </AbdsInput></td>
                            </tr>
                            <tr>
                                <td>
                                    <AbdsLabel></AbdsLabel>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="email"
                                        ref={rEmail}
                                        value={email}
                                        tooltip="Enter Email Here"
                                        placeholder="Enter Email Here">Email
                                </AbdsInput>
                                </td>
                                <td>
                                    <span className="required">*</span>
                                    <AbdsLabel>EDI Transactions</AbdsLabel>
                                    <div className="editransactionlist">{generateEdiTransactionCheckBoxList()}</div>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="duns"
                                        ref={rDuns}
                                        value={duns}
                                        tooltip="Enter DUNS Here"
                                        placeholder="Enter DUNS Here">DUNS
                                </AbdsInput></td>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="dea"
                                        ref={rDea}
                                        value={dea}
                                        tooltip="Enter DEA Here"
                                        placeholder="Enter DEA Here">DEA
                                    </AbdsInput></td>
                            </tr>
                            <tr>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="hin"
                                        ref={rHin}
                                        value={hin}
                                        tooltip="Enter HIN Here"
                                        placeholder="Enter HIN Here">HIN
                                    </AbdsInput></td>
                                <td>
                                    <span className="required">*</span>
                                    <AbdsLabel>PO Type</AbdsLabel>
                                    <div>
                                        <select ref={rPurchaseOrderType} value={purchaseOrderType} onChange={onChangePurchaseOrderType} name="potype" className="potype">
                                            <option key={0} value={0}>-- Choose PO Type --</option>
                                            <option key={1} value={52}>Regular</option>
                                            <option key={2} value={53}>Drop-Ship</option>
                                            <option key={3} value={54}>Both (Regular + Drop-Ship)</option>
                                            <option key={4} value={55}>CSOS</option>
                                        </select>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </fieldset>

                <fieldset className="vendor-tech">
                    <legend>EDI Envelope</legend>

                    <table className="absgtable noalternatecolor">
                        <tbody>
                            <tr>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="isaid"
                                        ref={rIsaId}
                                        value={isaId}
                                        tooltip="Enter ISA ID Here"
                                        placeholder="Enter ISA ID Here">ISA ID
                                    </AbdsInput></td>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="gsid"
                                        ref={rGsId}
                                        value={gsId}
                                        tooltip="Enter GS ID Here"
                                        placeholder="Enter GS ID Here">GS ID
                                    </AbdsInput></td>
                            </tr>
                            <tr>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="qualifier"
                                        ref={rQualifier}
                                        value={qualifier}
                                        tooltip="Enter Qualifier Here"
                                        placeholder="Enter Qualifier Here">Qualifier
                                    </AbdsInput>
                                </td>
                                <td>&nbsp;</td>
                            </tr>
                        </tbody>
                    </table>
                </fieldset>

                <fieldset className="vendor-tech">
                    <legend>Connection Details</legend>

                    <table className="absgtable noalternatecolor">
                        <tbody>
                            <tr>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="communicationProtocol"
                                        ref={rCommunicationProtocol}
                                        value={communicationProtocol}
                                        tooltip="Enter Communication Protocol Here"
                                        placeholder="Enter Communication Protocol Here">Communication Protocol
                                    </AbdsInput></td>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="van"
                                        ref={rVan}
                                        value={van}
                                        tooltip="Enter VAN Here"
                                        placeholder="Enter VAN Here">VAN
                                    </AbdsInput>
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <AbdsInput
                                        type="text"
                                        required
                                        width="full"
                                        name="as2"
                                        ref={rAs2}
                                        value={as2}
                                        tooltip="Enter AS2 Here"
                                        placeholder="Enter AS2 Here">AS2
                                    </AbdsInput>
                                </td>
                                <td>&nbsp;</td>
                            </tr>
                        </tbody>
                    </table>
                </fieldset>

                <hr />

                <div className="buttoncontainer">
                    <AbdsButton name="cancelsettings" disabled={disabled} ref={rClearVendorTechnicalDetails} onClick={onClickClearVendorTechnicalDetails} appearance="outline" palette="brand">Clear</AbdsButton>
                    &nbsp;&nbsp;&nbsp;
                    <AbdsButton disabled={disabled} onClick={onSubmitVendorTechnicalDetails} ref={rSubmitVendorTechnicalDetails}>Submit</AbdsButton>
                </div>
            </>)}

        </div>
    );
}